import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ProfileHeader from "./components/ProfileHeader";
import AthleteVitals from "./components/AthleteVitals";
import AcademicPanel from "./components/AcademicPanel";
import PerformanceStats from "./components/PerformanceStats";
import HighlightVideo from "./components/HighlightVideo";
import Events from "./components/Events";
import NotesSection from "./components/NotesSection";
import ContactInfo from "./components/ContactInfo";
import { generateMockAthleteProfile } from "./mockData";
import api, { getCurrentMode } from "../../../utils/api";
import { useAuth } from "../../../context/AuthContext";

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`athlete-tabpanel-${index}`}
      aria-labelledby={`athlete-tab-${index}`}
      {...other}
      style={{ paddingTop: "20px" }}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Helper function for tab accessibility
function a11yProps(index) {
  return {
    id: `athlete-tab-${index}`,
    "aria-controls": `athlete-tabpanel-${index}`,
  };
}

const EnhancedAthleteProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user: currentUser } = useAuth();

  // Refs for scrolling
  const headerRef = useRef(null);

  // State
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [lastVisited, setLastVisited] = useState(null);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Navigate to specific tab
  const navigateToTab = (tabIndex) => {
    setTabValue(tabIndex);
  };

  // Show notification
  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Handle note updates
  const handleNoteUpdate = (note) => {
    setAthlete((prev) => ({
      ...prev,
      notes: [...prev.notes.filter((n) => n.id !== note.id), note].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      ),
    }));
    showNotification("Note updated successfully");
  };

  // Handle adding athlete to board
  const handleAddToBoard = (boardId, columnId, boardName, columnName) => {
    // In a real app, this would make an API call to add the athlete to the specific column of the board
    // For now, we're just showing a notification
    console.log(
      `Adding ${athlete.firstName} ${athlete.lastName} to board ${boardId}, column ${columnId}`
    );

    const message =
      boardName && columnName
        ? `Added ${athlete.firstName} ${athlete.lastName} to ${boardName} - ${columnName}`
        : `Added ${athlete.firstName} ${athlete.lastName} to recruiting board`;

    showNotification(message);

    // You could also navigate to the board page
    // navigate(`/boards?boardId=${boardId}`);
  };

  // Schedule Visit and Send Message functionality removed

  // Handle athlete updates
  const handleUpdateAthlete = async (updatedAthlete) => {
    try {
      if (getCurrentMode() === "sandbox") {
        // Sandbox mode - just update local state
        setAthlete(updatedAthlete);
        showNotification("Athlete updated successfully");
      } else {
        // Development mode - update in MongoDB
        const response = await api.prospects.update(id, updatedAthlete);
        // Update the athlete with the response data to get any server-side changes
        setAthlete(response);
        showNotification("Athlete updated successfully");
      }
    } catch (error) {
      console.error("Error updating athlete:", error);
      // Extract more detailed error information if available
      let errorMessage = "Failed to update athlete";
      if (error.response?.data?.error) {
        errorMessage = `Error: ${error.response.data.error}`;
        if (error.response.data.details) {
          if (typeof error.response.data.details === "string") {
            errorMessage += ` - ${error.response.data.details}`;
          } else {
            errorMessage += ` - ${JSON.stringify(error.response.data.details)}`;
          }
        }
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      showNotification(errorMessage, "error");
    }
  };

  // Handle events update
  const handleEventsUpdate = async (event) => {
    try {
      if (getCurrentMode() === "sandbox") {
        // Sandbox mode - just update local state
        setAthlete((prev) => ({
          ...prev,
          events: [...prev.events.filter((e) => e.id !== event.id), event].sort(
            (a, b) => new Date(a.date) - new Date(b.date)
          ),
        }));
        showNotification("Events updated");
      } else {
        // Development mode - update in MongoDB
        if (event.deleted) {
          // Event was deleted, remove from local state
          setAthlete((prev) => ({
            ...prev,
            events: prev.events.filter((e) => e.id !== event.id),
          }));
          showNotification("Event deleted successfully");
        } else {
          // Event was added/updated, refresh from API
          const response = await api.prospects.getById(id);
          setAthlete((prev) => ({
            ...prev,
            events: response.events || [],
          }));
          showNotification("Events updated successfully");
        }
      }
    } catch (error) {
      console.error("Error updating events:", error);
      showNotification("Error updating events", "error");
    }
  };

  // Handle stats update
  const handleStatsUpdate = async (statsData) => {
    try {
      if (getCurrentMode() === "sandbox") {
        // Sandbox mode - just update local state
        setAthlete((prev) => ({
          ...prev,
          stats: { ...prev.stats, ...statsData },
        }));
        showNotification("Stats updated successfully");
      } else {
        // Development mode - update in MongoDB
        const updatedAthlete = {
          ...athlete,
          stats: { ...athlete.stats, ...statsData },
        };
        const response = await api.prospects.update(id, updatedAthlete);
        setAthlete(response);
        showNotification("Stats updated successfully");
      }
    } catch (error) {
      console.error("Error updating stats:", error);
      showNotification("Error updating stats", "error");
    }
  };

  // Handle contact updates
  const handleContactUpdate = async (contactData) => {
    try {
      if (getCurrentMode() === "sandbox") {
        // Sandbox mode - just update local state
        setAthlete((prev) => {
          const updatedContacts = [...(prev.contacts || [])];

          if (contactData.deleted) {
            const filteredContacts = updatedContacts.filter(
              (c) =>
                !(
                  c.id === contactData.id ||
                  c._id === contactData.id ||
                  c.name === contactData.name
                )
            );
            return { ...prev, contacts: filteredContacts };
          } else if (contactData.id) {
            const contactIndex = updatedContacts.findIndex(
              (c) =>
                c.id === contactData.id ||
                c._id === contactData.id ||
                c.name === contactData.name
            );
            if (contactIndex >= 0) {
              updatedContacts[contactIndex] = contactData;
            } else {
              updatedContacts.push(contactData);
            }
          } else {
            // Add new contact
            updatedContacts.push(contactData);
          }

          return { ...prev, contacts: updatedContacts };
        });
        showNotification("Contact updated successfully");
      } else {
        // Development mode - update in MongoDB
        const updatedAthlete = { ...athlete };

        if (contactData.deleted) {
          // Remove deleted contact - use multiple identifiers for lookup
          updatedAthlete.contacts = (updatedAthlete.contacts || []).filter(
            (c) =>
              !(
                c.id === contactData.id ||
                c._id === contactData.id ||
                c.name === contactData.name
              )
          );
        } else if (contactData.id) {
          // Update existing contact - use multiple identifiers for lookup
          const contactIndex = (updatedAthlete.contacts || []).findIndex(
            (c) =>
              c.id === contactData.id ||
              c._id === contactData.id ||
              c.name === contactData.name
          );
          if (contactIndex >= 0) {
            updatedAthlete.contacts[contactIndex] = contactData;
          } else {
            updatedAthlete.contacts = [
              ...(updatedAthlete.contacts || []),
              contactData,
            ];
          }
        } else {
          // Add new contact - remove any ID field to let MongoDB generate ObjectId
          const { id, ...contactWithoutId } = contactData;
          updatedAthlete.contacts = [
            ...(updatedAthlete.contacts || []),
            contactWithoutId,
          ];
        }

        const response = await api.prospects.update(id, updatedAthlete);
        setAthlete(response);
        showNotification("Contact updated successfully");
      }
    } catch (error) {
      console.error("Error updating contact:", error);
      showNotification("Failed to update contact", "error");
    }
  };

  // Handle communication updates
  const handleCommunicationUpdate = async (communicationData) => {
    try {
      if (getCurrentMode() === "sandbox") {
        // Sandbox mode - just update local state
        setAthlete((prev) => {
          const updatedCommunications = [...(prev.communications || [])];

          if (communicationData.id) {
            const commIndex = updatedCommunications.findIndex(
              (c) =>
                c.id === communicationData.id || c._id === communicationData.id
            );
            if (commIndex >= 0) {
              updatedCommunications[commIndex] = communicationData;
            } else {
              updatedCommunications.push(communicationData);
            }
          } else {
            // Add new communication
            updatedCommunications.push(communicationData);
          }

          return { ...prev, communications: updatedCommunications };
        });
        showNotification("Communication logged successfully");
      } else {
        const updatedAthlete = { ...athlete };

        if (communicationData.id) {
          const commIndex = (updatedAthlete.communications || []).findIndex(
            (c) =>
              c.id === communicationData.id || c._id === communicationData.id
          );
          if (commIndex >= 0) {
            updatedAthlete.communications[commIndex] = communicationData;
          } else {
            updatedAthlete.communications = [
              ...(updatedAthlete.communications || []),
              communicationData,
            ];
          }
        } else {
          const { id, ...communicationWithoutId } = communicationData;
          updatedAthlete.communications = [
            ...(updatedAthlete.communications || []),
            communicationWithoutId,
          ];
        }

        const response = await api.prospects.update(id, updatedAthlete);
        setAthlete(response);
        showNotification("Communication logged successfully");
      }
    } catch (error) {
      console.error("Error updating communication:", error);
      showNotification("Failed to log communication", "error");
    }
  };

  // Fetch athlete data
  useEffect(() => {
    const fetchAthleteData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (getCurrentMode() === "sandbox") {
          // Sandbox mode - use mock data
          setTimeout(() => {
            try {
              const mockData = generateMockAthleteProfile(id);

              // Ensure the mockData has socialMedia field for compatibility with our components
              if (!mockData.socialMedia) {
                mockData.socialMedia = {
                  twitter: mockData.contact?.twitter || "@athlete",
                  instagram: mockData.contact?.instagram || "@athlete",
                  facebook: null,
                  linkedin: null,
                };
              }

              setAthlete(mockData);
              setLoading(false);
            } catch (err) {
              setError("Failed to generate mock athlete profile");
              setLoading(false);
            }
          }, 1000);
        } else {
          // Development mode - fetch from MongoDB
          const prospectData = await api.prospects.getById(id);

          // Transform MongoDB data to enhanced profile format
          const transformedAthlete = {
            id: prospectData._id || prospectData.id,
            firstName: prospectData.firstName || "Unknown",
            lastName: prospectData.lastName || "Athlete",
            position: prospectData.position || null,
            secondaryPosition: prospectData.secondaryPositions?.[0] || null,
            graduationYear:
              prospectData.graduationYear || new Date().getFullYear() + 1,
            school: prospectData.highSchool || null,
            highSchool: prospectData.highSchool || null,
            city: prospectData.address?.city || null,
            state: prospectData.address?.state || null,
            address: prospectData.address || {},
            height: prospectData.height || null,
            weight: prospectData.weight || null,
            age: prospectData.age || null,
            dateOfBirth: prospectData.dateOfBirth || null,
            standingReach: prospectData.standingReach || null,
            benchPress: prospectData.benchPress || null,
            // rating field removed - no longer displaying star ratings
            recruitingStatus: prospectData.recruitingStatus || "prospect",
            email: prospectData.email || null,
            phone: prospectData.phone || null,
            // Add speedData to match the model schema
            speedData: {
              fortyYard: prospectData.speedData?.fortyYard || null,
              shuttleRun: prospectData.speedData?.shuttleRun || null,
              sixtyYardDash: prospectData.speedData?.sixtyYardDash || null,
              hundredMeter: prospectData.speedData?.hundredMeter || null,
            },
            // Map legacy fields for backward compatibility
            fortyYardDash: prospectData.speedData?.fortyYard || null,
            shuttleRun: prospectData.speedData?.shuttleRun || null,
            wingspan: prospectData.wingspan || null,
            verticalJump: prospectData.verticalJump || null,
            academics: {
              gpa: prospectData.academics?.gpa || null,
              coreGpa: prospectData.academics?.coreGpa || null,
            },
            socialMedia: {
              twitter: prospectData.socialMedia?.twitter || null,
              instagram: prospectData.socialMedia?.instagram || null,
              facebook: prospectData.socialMedia?.facebook || null,
              linkedin: prospectData.socialMedia?.linkedin || null,
              hudl: prospectData.socialMedia?.hudl || [],
              youtube: prospectData.socialMedia?.youtube || [],
            },
            contact: {
              email: prospectData.email || null,
              phone: prospectData.phone || null,
              twitter: prospectData.socialMedia?.twitter || null,
              instagram: prospectData.socialMedia?.instagram || null,
            },
            notes:
              prospectData.notes?.map((note, index) => ({
                id: note._id || index,
                text: note.text,
                category: note.category || "general",
                date: note.createdAt || new Date().toISOString(),
                author: "Coach", // Default since we don't have user info here
              })) || [],
            contacts:
              prospectData.contacts?.map((contact) => ({
                id: contact._id || contact.id,
                relationship: contact.relationship || "other",
                name: contact.name || "Unknown Contact",
                phone: contact.phone || "",
                email: contact.email || "",
                notes: contact.notes || "",
              })) || [],
            communications:
              prospectData.communications?.map((comm) => ({
                id: comm._id || comm.id,
                contactId:
                  comm.contactId || comm.contact?._id || comm.contact?.id || "",
                type: comm.type || "phone",
                date: comm.date || comm.createdAt || new Date().toISOString(),
                notes: comm.notes || "",
                outcome: comm.outcome || "completed",
                followUpNeeded: comm.followUpNeeded || false,
                followUpDate: comm.followUpDate || null,
              })) || [],
            events: prospectData.events || [], // MongoDB prospects now have events
            stats: prospectData.stats || {}, // Pass the actual stats object
            highlights: [], // MongoDB prospects don't have highlights yet
            profileImage: prospectData.profilePicture || null,
            favorite: false,
            createdAt: prospectData.createdAt,
            updatedAt: prospectData.updatedAt,
            verification: prospectData.verification || {},
          };

          setAthlete(transformedAthlete);
          setLoading(false);
        }

        // Check if this athlete was visited before
        const lastVisitTime = localStorage.getItem(`athlete_${id}_last_visit`);
        if (lastVisitTime) {
          setLastVisited(new Date(lastVisitTime));
        }

        // Update last visit time
        localStorage.setItem(
          `athlete_${id}_last_visit`,
          new Date().toISOString()
        );
      } catch (err) {
        console.error("Error fetching athlete data:", err);
        setError(
          err.message || "Failed to load athlete profile. Please try again."
        );
        setLoading(false);
      }
    };

    fetchAthleteData();

    // Keyboard shortcuts (only in sandbox mode for now)
    const handleKeyDown = (e) => {
      // Only if not in an input field
      if (e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA") {
        // Ctrl+N for new note
        if (e.ctrlKey && e.key === "n") {
          e.preventDefault();
          setTabValue(4); // Notes tab
          // Add logic to focus on new note field
        }

        // Keyboard shortcut for message removed
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, textAlign: "center", py: 8 }}>
        <CircularProgress size={60} />
        <Box sx={{ mt: 3 }}>
          <h2>Loading Athlete Profile</h2>
          <p>Retrieving comprehensive athlete information...</p>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <h2>Something went wrong</h2>
          <p>
            We couldn't load this athlete's profile. Please try again later.
          </p>
          <button
            onClick={() => navigate("/prospects")}
            style={{
              padding: "10px 20px",
              marginTop: "20px",
              cursor: "pointer",
            }}
          >
            Return to Prospects List
          </button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
      <Box
        ref={headerRef}
        sx={{
          position: "static",
          zIndex: 1,
          bgcolor: "background.paper",
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <ProfileHeader
          athlete={athlete}
          onBack={() => navigate("/prospects")}
          onAddToBoard={handleAddToBoard}
          onUpdateAthlete={handleUpdateAthlete}
          lastVisited={lastVisited}
        />

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          aria-label="athlete profile tabs"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "medium",
              fontSize: "0.95rem",
            },
          }}
        >
          <Tab label="Overview" {...a11yProps(0)} />
          <Tab label="Performance" {...a11yProps(1)} />
          <Tab label="Academics" {...a11yProps(2)} />
          <Tab label="Highlights" {...a11yProps(3)} />
          <Tab label="Notes" {...a11yProps(4)} />
          <Tab label="Events" {...a11yProps(5)} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mt: 2, borderRadius: 2 }}>
        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
            }}
          >
            <Box sx={{ flex: "1 1 60%" }}>
              <AthleteVitals
                athlete={athlete}
                onUpdateAthlete={handleUpdateAthlete}
                onNavigateToNotes={() => navigateToTab(4)}
                currentUser={currentUser}
              />
            </Box>
            <Box sx={{ flex: "1 1 40%" }}>
              <ContactInfo
                athlete={athlete}
                contacts={athlete.contacts || []}
                communications={athlete.communications || []}
                onContactUpdate={handleContactUpdate}
                onCommunicationUpdate={handleCommunicationUpdate}
              />
              {/* <Box sx={{ mt: 3 }}>
                <SimilarAthletes
                  athlete={athlete}
                  onAthleteClick={(athleteId) =>
                    navigate(`/prospects/profile/${athleteId}`)
                  }
                />
              </Box> */}
            </Box>
          </Box>
        </TabPanel>

        {/* Performance Tab */}
        <TabPanel value={tabValue} index={1}>
          <PerformanceStats
            athlete={athlete}
            onStatsUpdate={handleStatsUpdate}
          />
        </TabPanel>

        {/* Academics Tab */}
        <TabPanel value={tabValue} index={2}>
          <AcademicPanel
            athlete={athlete}
            onUpdateAthlete={handleUpdateAthlete}
          />
        </TabPanel>

        {/* Highlights Tab */}
        <TabPanel value={tabValue} index={3}>
          <HighlightVideo athlete={athlete} />
        </TabPanel>

        {/* Notes Tab */}
        <TabPanel value={tabValue} index={4}>
          <NotesSection
            athlete={athlete}
            notes={athlete.notes}
            onNoteUpdate={handleNoteUpdate}
          />
        </TabPanel>

        {/* Scholarship Tab */}
        <TabPanel value={tabValue} index={5}>
          <Events
            events={athlete.events}
            onUpdateEvent={handleEventsUpdate}
            prospectId={id}
          />
        </TabPanel>
      </Paper>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EnhancedAthleteProfile;
